import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import Spinner from "../../components/Shared/Spinner";
import { GetAllIFRS } from "../../api/Quiz/GetAllIFRS";
import IFRSExamsTable from "../../components/Dashboard/IFRSExams/IFRSExamsTable";
import Button from "../../components/Shared/Button";
import AddIcon from "../../components/Icons/AddIcon";

const IFRSExams = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"asc" | "desc">("asc");
  const [jumpToPage, setJumpToPage] = useState("");
  const { courseId } = useParams();
  const limit = 10;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: IFRSResponse, isLoading: isIFRSLoading } = useQuery({
    queryFn: () => GetAllIFRS({ page, limit, sort, courseId, search }),
    queryKey: ["ifrs-exams", page, limit, sort, search],
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  const exams = IFRSResponse?.data?.data || [];
  const meta = IFRSResponse?.data?.meta;

  const getPageNumbers = (): (number | "...")[] => {
    if (!meta || meta.lastPage <= 0) return [];
    const total = meta.lastPage;
    const current = meta.page;
    const delta = 2;
    const pages: (number | "...")[] = [];

    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push("...");
    if (total > 1) pages.push(total);

    return pages;
  };

  if (isIFRSLoading && !search && page === 1 && sort === "asc") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-7">IFRS Exams</h1>
      <div className="p-8 rounded-xl bg-white border-black/15 shadow">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-bold text-2xl">All IFRS Exams</h1>
          <Link to="create">
            <Button
              className="!h-12 w-48 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
              variant="primary"
            >
              <AddIcon className="mr-1.5" />
              Create IFRS Exam
            </Button>
          </Link>
        </div>

        {/* Search and Sort Controls */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search exams..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ×
              </button>
            )}
          </div>
          <button
            onClick={() => {
              setSort((s) => (s === "asc" ? "desc" : "asc"));
              setPage(1);
            }}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors select-none"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={sort === "desc" ? "rotate-180 transition-transform" : "transition-transform"}
            >
              <path d="M3 8l4-4 4 4M7 4v16M13 16l4 4 4-4M17 20V4" />
            </svg>
            {sort === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>

        {isIFRSLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <IFRSExamsTable data={exams} />
        )}

        {meta && (
          <div className="flex justify-between items-center mt-6 flex-wrap gap-3">
            <div className="text-sm text-gray-600">
              Showing {exams.length} of {meta.total} exams
            </div>
            <div className="flex items-center gap-1">
              {/* First page */}
              <button
                onClick={() => setPage(1)}
                disabled={!meta.hasPreviousPage || isIFRSLoading}
                title="First page"
                className="h-9 w-9 flex items-center justify-center text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                «
              </button>
              {/* Previous */}
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={!meta.hasPreviousPage || isIFRSLoading}
                title="Previous page"
                className="h-9 w-9 flex items-center justify-center text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                ‹
              </button>
              {/* Page numbers with ellipsis */}
              {getPageNumbers().map((p, i) =>
                p === "..." ? (
                  <span
                    key={`ellipsis-${i}`}
                    className="h-9 w-8 flex items-center justify-center text-sm text-gray-400"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p as number)}
                    disabled={isIFRSLoading}
                    className={`h-9 min-w-[36px] px-2 text-sm border rounded-md transition-colors disabled:cursor-not-allowed ${
                      meta.page === p
                        ? "bg-blue-500 text-white border-blue-500"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              {/* Next */}
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!meta.hasNextPage || isIFRSLoading}
                title="Next page"
                className="h-9 w-9 flex items-center justify-center text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                ›
              </button>
              {/* Last page */}
              <button
                onClick={() => setPage(meta.lastPage)}
                disabled={!meta.hasNextPage || isIFRSLoading}
                title="Last page"
                className="h-9 w-9 flex items-center justify-center text-sm border rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                »
              </button>
              {/* Jump to page */}
              <div className="flex items-center gap-1 ms-2">
                <input
                  type="number"
                  min={1}
                  max={meta.lastPage}
                  value={jumpToPage}
                  onChange={(e) => setJumpToPage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const target = parseInt(jumpToPage, 10);
                      if (!isNaN(target) && target >= 1 && target <= meta.lastPage) {
                        setPage(target);
                        setJumpToPage("");
                      }
                    }
                  }}
                  placeholder="Page #"
                  className="h-9 w-20 text-center text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  onClick={() => {
                    const target = parseInt(jumpToPage, 10);
                    if (!isNaN(target) && target >= 1 && target <= meta.lastPage) {
                      setPage(target);
                      setJumpToPage("");
                    }
                  }}
                  disabled={isIFRSLoading}
                  className="h-9 px-3 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Jump To
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IFRSExams;

